/**
 * Consumer WebSocket - OPBridge `/ws/consumer` silent-push client (Phase B).
 *
 * Phase A (CP-051) made pairing a one-time act; Phase B keeps the paired set
 * live WITHOUT re-pairing:
 *   - `inheritance_available` push: a PEER bridge minted a bearer for this
 *     (user_id, consumer). We stash it so a later change-primary needs no
 *     re-pair. This is the real silent-push half.
 *   - reconnect-resync: on every (re)connect we backfill via
 *     `GET /pairing/inheritances?since=<cursor>` so nothing minted while the
 *     socket was down is missed.
 *   - revocation: OPBridge now ALSO pushes `bearer_revoked` (shipped
 *     2026-07-29, confirmed live in running commit `fa882ae` / v1.15.36 —
 *     OPBridge S175 2026-08-02) the instant this socket's own bearer is
 *     revoked (matched server-side on `prefix_id`, so it only ever arrives
 *     for OUR bearer). We still keep the resync GET's 401 as the fallback
 *     revocation signal for whenever the socket itself is down — the push
 *     just turns "within ~45s, or on next resume" into "immediately" while
 *     the tab is open.
 *   - `pwa_action` push: a Patchbay rule's `pwa.*` action addressed to this
 *     (user_id, consumer_id) — the "B path" for inbound-MIDI-triggers-button
 *     bindings that need the tab open (toggle-mode buttons, macros/
 *     sequencers/LFOs — anything not statically resolvable at Learn time).
 *     Routed to `onPwaAction()` listeners. The one command with a listener
 *     is a timeline cue's `timeline_light` (DEC-269, `timeline/canvasLights.ts`);
 *     the Patchbay Learn half of the B-path is still unbuilt (OPButtons
 *     2026-07-20 / OPBridge S155). A timeline plays on its HOME bridge
 *     (DEC-224), which this primary-only socket cannot hear, so
 *     `opbridge/peerPwaSockets.ts` listens for `pwa_action` on every other
 *     paired bridge and feeds the same listeners through `emitPwaAction`.
 *
 * Shape mirrors the pollers (clockPoll.ts / enginesStatePoll.ts): a
 * module-singleton with bare start/stop/poke functions, idempotent. It follows
 * the PRIMARY only (DEC-063) and re-binds itself when the primary's id / bearer
 * / baseUrl changes (setPrimary deliberately skips reconcile, so nothing in the
 * connection layer would move the socket for us).
 *
 * Auth is the browser subprotocol smuggle - `new WebSocket(url, ['opbridge.v1',
 * '<opb_bearer>'])` - the only way to auth a WS from a browser (no request
 * headers). A revoked/unknown bearer 401s the UPGRADE, which the browser
 * surfaces only as an opaque error/close (no status); that's exactly why the
 * HTTP resync GET is the authoritative revocation signal, and why we run it
 * BEFORE opening the socket on each attempt (breaks the open-401-reopen loop).
 *
 * The clock stays on clockPoll.ts's `GET /clock/status` this session; the
 * `clock_marker` frames that ride this socket are routed to an optional bus
 * for a future Build-2a consumer and otherwise ignored.
 */

import { createSignal, createEffect, createMemo, createRoot, on, type Accessor } from 'solid-js';
import { primaryBridge, getConnection } from './store.ts';
import type { BridgeConfig } from './storage.ts';
import { OpbridgeError, type PairingInheritance } from './httpClient.ts';
import { OPBRIDGE_SUBPROTOCOL } from './opbridgeConfig.ts';
import { recordSetlistState } from '../setlist/state.ts';
import type { SetlistStateResponse } from './httpClient.ts';
import { watchJob } from '../diag/watchdog.ts';
import { ingestOpvStatus } from '../opvisual/live.ts';

// Reconnect backoff: exponential with a 10 s cap + jitter so a fleet of iPads
// reconnecting in lockstep after an OPBridge restart doesn't thundering-herd.
const RECONNECT_BASE_MS = 1_000;
const RECONNECT_CAP_MS = 10_000;
const RECONNECT_JITTER = 0.3;
// A socket must stay open at least this long before its close resets the
// backoff counter - otherwise an open-then-instant-drop flap would reset to 0
// every cycle and hot-loop at ~1s, defeating the backoff.
const STABLE_UPTIME_MS = 5_000;
// While connected, re-run the resync GET on a slow cadence. Its real job here
// is revocation detection (there is no revoke push, and a revoke does NOT
// force-close an open socket), plus catching any inheritance the push missed.
const PROBE_INTERVAL_MS = 45_000;
// While revoked, slowly re-probe so an operator re-approval (or a same-bearer
// recovery) is picked up without needing a full re-pair.
const REVOKED_REPROBE_MS = 60_000;

// ─── reactive surface (read by the badge, Connect modal, App prompt) ────────

const [connectedSig, setConnectedSig] = createSignal(false);
const [revokedSig, setRevokedSig] = createSignal(false);
const [deniedSig, setDeniedSig] = createSignal(false);
const [inheritancesSig, setInheritancesSig] = createSignal<PairingInheritance[]>([]);
const [lastInheritanceAtSig, setLastInheritanceAtSig] = createSignal<number | null>(null);

/** True while the `/ws/consumer` socket to the primary is open. Diagnostic /
 *  test surface - the pairing feature degrades silently when false. */
export const consumerWsConnected: Accessor<boolean> = connectedSig;

/** True when the primary's bearer has been revoked/invalidated (a 401 on the
 *  resync GET). Drives the badge alert + the "re-pair" prompt. Re-derived from
 *  the server every session - never persisted. */
export const consumerRevoked: Accessor<boolean> = revokedSig;

/** True when the primary's bearer is LIVE but does not carry a scope we need
 *  (DEC-568 `scope_not_granted`). Deliberately separate from
 *  `consumerRevoked`, because the operator-facing remedies are opposites:
 *  revoked resolves itself via a credential refresh, whereas this one never
 *  will - the grant was never issued, so it needs a re-pair against a bundle
 *  that includes the scope. Showing "reconnecting..." for this would be a lie
 *  that never resolves. Re-derived every session, never persisted. */
export const consumerDenied: Accessor<boolean> = deniedSig;

/** Peer-bridge bearers inherited via silent-push + resync, newest-first. The
 *  change-primary flow can promote one of these without re-pairing; the Connect
 *  modal surfaces the count. The full resync (getInheritances(0)) REPLACES this
 *  set, so entries the server drops (e.g. across a daemon restart) are pruned;
 *  a still-registered-but-offline peer only drops once its server record goes
 *  (revocation/restart), not on mere unreachability. */
export const availableInheritances: Accessor<PairingInheritance[]> = inheritancesSig;

/** Wall-clock ms of the most recent NEW inheritance (a fresh peer bearer), for
 *  a transient "new connection available" affordance. Null until one arrives. */
export const lastInheritanceAt: Accessor<number | null> = lastInheritanceAtSig;

/** Look up an inherited bearer by peer instance name (change-primary consults
 *  this to skip re-pairing). Null when none is known. Scans values because the
 *  registry is keyed by the stable `node_id` (DEC-465), not the mDNS label. */
export function inheritedBearerFor(instanceName: string): PairingInheritance | null {
  if (!instanceName) return null;
  for (const inh of registry.values()) {
    if (inh.bridge_instance_name === instanceName) return inh;
  }
  return null;
}

/** Look up an inherited bearer by the minting bridge's stable `node_id`
 *  (DEC-465). Preferred over {@link inheritedBearerFor} — node_id is the
 *  stable key. Null when none is known. */
export function inheritedBearerForNode(nodeId: string): PairingInheritance | null {
  if (!nodeId) return null;
  return registry.get(nodeId) ?? null;
}

// ─── module state ───────────────────────────────────────────────────────────

interface Bound { id: string; bearer: string; baseUrl: string }

let socket: WebSocket | null = null;
let bound: Bound | null = null;               // what the socket is (re)connecting to
let alive = false;                            // start called, not yet stopped
let reconnectAttempt = 0;
let openedAtMs = 0;                           // wall-clock ms the live socket opened (0 = not open)
let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
let probeTimer: ReturnType<typeof setInterval> | null = null;
let revokedTimer: ReturnType<typeof setInterval> | null = null;
let cursorUs = 0;                             // diagnostic high-water (newest minted_at_us seen); fetches always use since=0
let disposeRoot: (() => void) | null = null;
const registry = new Map<string, PairingInheritance>();  // instance_name -> inheritance
/** Optional listeners for `clock_marker` (Build 2a hook). Kept minimal. */
const clockMarkerListeners = new Set<(m: Record<string, unknown>) => void>();
/** Optional listeners for `pwa_action` (the Patchbay B-path). Kept minimal —
 *  see the module doc comment; there is no command dispatcher yet. */
const pwaActionListeners = new Set<(e: PwaActionEvent) => void>();
const timelineFiredListeners = new Set<(e: TimelineFiredEvent) => void>();
/** Optional listeners for `doc_updated` - the doc-store change notification
 *  that lets `layout/projectSync.ts` react in ~50ms instead of waiting out a
 *  poll interval. */
const docUpdatedListeners = new Set<(e: DocUpdatedEvent) => void>();
/** Listeners for `performance_mode_changed` - `opbridge/performanceMode.ts`. */
const performanceModeListeners = new Set<(e: PerformanceModeChangedEvent) => void>();

// ─── public lifecycle ───────────────────────────────────────────────────────

/** Start following the primary. Idempotent. Opens (and keeps open) one
 *  `/ws/consumer` socket to whichever bridge is primary, re-binding when the
 *  primary's identity/bearer/URL changes. */
export function startConsumerWs(): void {
  if (alive) return;
  alive = true;
  // Own the primary-watcher effect in a detached root so we can dispose it in
  // stop(). `on(key, cb)` fires the callback only when the derived key changes,
  // so an unrelated bridge edit (identity backfill, a peer row) won't churn the
  // socket. The callback runs untracked, so re-reading primaryBridge() is safe.
  createRoot((dispose) => {
    disposeRoot = dispose;
    // Value-gate the key with a memo: Solid's on(deps, cb) runs cb on every
    // dep change WITHOUT comparing the derived value, so a bare primaryKey
    // function would rebind (closeSocket + resync + WS reopen) on EVERY
    // bridgesSig write - identity backfills, peer rows, renames. The memo only
    // notifies when the id/bearer/baseUrl string actually changes.
    const key = createMemo(primaryKey);
    createEffect(on(key, () => { rebindTo(primaryBridge()); }));
  });
}

/** Stop and fully tear down. Used by App onCleanup + tests. */
export function stopConsumerWs(): void {
  alive = false;
  closeSocket();
  stopRevokedReprobe();
  if (disposeRoot) { disposeRoot(); disposeRoot = null; }
  bound = null;
  registry.clear();
  pushRegistry();
  setRevokedSig(false);
  setDeniedSig(false);
  setConnectedSig(false);
}

/** Force an immediate resync + reconnect. Called on foreground/resume and when
 *  the Connect modal opens (so the change-primary candidate list is fresh even
 *  if the socket silently died). No-op when unbound. */
export function pokeConsumerWs(): void {
  if (!alive || !bound) return;
  // Denied is terminal for THIS bearer - a poke cannot conjure a grant that was
  // never issued. Only a re-pair or a rebind clears it, and both replace `bound`.
  if (deniedSig()) return;
  const b = bound;
  if (revokedSig()) {
    // Revoked: force an immediate recovery re-check (the operator may have
    // re-approved) instead of waiting out the 60s revoked re-probe. attemptConnect
    // can't do this - it early-returns while revoked.
    void resyncAndProbe(b).then((res) => {
      if (bound !== b || res !== 'ok') return;
      stopRevokedReprobe();
      setRevokedSig(false);
      reconnectAttempt = 0;
      attemptConnect();
    });
    return;
  }
  if (socket && socket.readyState === WebSocket.OPEN) {
    // Live socket - just refresh the registry + re-check revocation.
    void resyncAndProbe(b).then((res) => {
      if (bound !== b) return;
      if (res === 'revoked') onRevoked(b);
      else if (res === 'denied') onDenied(b);
    });
    return;
  }
  // Not open - retry now instead of waiting out the backoff.
  if (reconnectTimer) { clearTimeout(reconnectTimer); reconnectTimer = null; }
  reconnectAttempt = 0;
  attemptConnect();
}

// ─── primary binding ────────────────────────────────────────────────────────

/** Reactive key for the primary. '' when there's no primary or it has no
 *  bearer (unpaired). Changing it re-binds the socket. */
function primaryKey(): string {
  const p = primaryBridge();
  if (!p || !p.bearer) return '';
  return `${p.id} ${p.bearer} ${p.baseUrl}`;
}

function rebindTo(p: BridgeConfig | null): void {
  closeSocket();
  stopRevokedReprobe();
  if (!p || !p.bearer) {
    // Unpaired / no primary - nothing to follow. A missing bearer can't be
    // "revoked", so clear that state.
    bound = null;
    registry.clear();
    pushRegistry();
    setRevokedSig(false);
    setDeniedSig(false);
    setConnectedSig(false);
    return;
  }
  bound = { id: p.id, bearer: p.bearer, baseUrl: stripSlash(p.baseUrl) };
  cursorUs = 0;   // reset the diagnostic high-water; fetches use since=0 regardless
  registry.clear();
  pushRegistry();
  setRevokedSig(false);
  setDeniedSig(false);   // a different bearer may well carry the scope
  reconnectAttempt = 0;
  attemptConnect();
}

// ─── connect state machine ──────────────────────────────────────────────────

/** Probe (HTTP) then open (WS). The HTTP probe runs first on every attempt: it
 *  resyncs inheritances AND is the only reliable revocation signal (a revoked
 *  bearer 401s here but only opaquely-fails the WS upgrade). */
function attemptConnect(): void {
  if (!alive || !bound || revokedSig() || deniedSig()) return;
  const b = bound;
  void resyncAndProbe(b).then((res) => {
    if (!alive || bound !== b) return;   // rebound mid-probe - abandon
    if (res === 'revoked') { onRevoked(b); return; }
    // Before this arm existed, 'denied' fell through to openSocket() and the
    // socket then failed opaquely, so the whole cycle repeated forever.
    if (res === 'denied') { onDenied(b); return; }
    if (res === 'error') { scheduleReconnect(); return; }  // transient - back off + retry
    openSocket(b);
  });
}

function openSocket(b: Bound): void {
  // ONE live socket per binding (2026-10-11, found by the DEC-269 smoke). A
  // `pokeConsumerWs()` that lands while the first attempt's probe is in
  // flight (socket still null) starts a second attempt; both probes then
  // reach here. Before this guard the second opened another socket, the
  // first stayed open as an orphan, and EVERY frame (midi_in, pwa_action,
  // doc_updated, setlist_update) was delivered twice - measured: two sockets
  // to the primary 12 ms apart on a plain boot, with no DEC-269 code running.
  if (socket && bound === b && (socket.readyState === WebSocket.OPEN || socket.readyState === WebSocket.CONNECTING)) return;
  const wsUrl = `${b.baseUrl.replace(/^http/i, 'ws')}/ws/consumer`;
  let sock: WebSocket;
  try {
    sock = new WebSocket(wsUrl, [OPBRIDGE_SUBPROTOCOL, b.bearer]);
  } catch {
    scheduleReconnect();
    return;
  }
  socket = sock;

  sock.addEventListener('open', () => {
    if (bound !== b || socket !== sock) { try { sock.close(); } catch { /* */ } return; }
    midiInPushSeen = false;
    openedAtMs = Date.now();
    setConnectedSig(true);
    log('connected');
    startProbeTimer();
  });

  sock.addEventListener('message', (ev) => {
    // Only the CURRENT socket delivers. The close handler below already
    // ignored a superseded socket; the message handler did not, which is how
    // an orphan doubled every frame (see openSocket's guard).
    if (bound !== b || socket !== sock) return;
    handleMessage(typeof ev.data === 'string' ? ev.data : '');
  });

  sock.addEventListener('close', () => {
    // Ignore a superseded/orphan socket's close entirely. closeSocket() nulls
    // `socket` before close(), and a stale sock (after rebind/double-open) is
    // never the current one - either way this must be a no-op so it can't kill
    // the LIVE socket's probe timer or flip its connected state.
    if (socket !== sock) return;
    socket = null;
    midiInPushSeen = false;
    stopProbeTimer();
    setConnectedSig(false);
    if (alive && bound === b && !revokedSig() && !deniedSig()) {
      // Reset the backoff only if the socket was stably open; an
      // open-then-instant-drop flap keeps the attempt count growing so it
      // backs off instead of hot-looping at ~1s.
      if (openedAtMs && Date.now() - openedAtMs >= STABLE_UPTIME_MS) reconnectAttempt = 0;
      openedAtMs = 0;
      log('reconnecting');
      scheduleReconnect();
    } else {
      openedAtMs = 0;
    }
  });

  // 'error' always precedes 'close'; let the close handler own reconnect.
  sock.addEventListener('error', () => { /* handled by close */ });
}

function scheduleReconnect(): void {
  if (!alive || !bound || revokedSig() || deniedSig() || reconnectTimer) return;
  const base = Math.min(RECONNECT_BASE_MS * Math.pow(1.6, reconnectAttempt), RECONNECT_CAP_MS);
  const delay = base + base * RECONNECT_JITTER * Math.random();
  reconnectAttempt += 1;
  reconnectTimer = setTimeout(() => { reconnectTimer = null; attemptConnect(); }, delay);
}

/** GET /pairing/inheritances - resync + revocation probe. Returns:
 *  'ok' (registry replaced with the server's full set), 'revoked' (the
 *  credential is no good - go and get another), 'denied' (the credential is
 *  live and simply lacks the grant - surface, never retry), or 'error'
 *  (transient network/timeout/5xx - caller keeps retrying).
 *
 *  'denied' is the fourth arm added 2026-09-07. Before it, `scope_not_granted`
 *  fell through to 'error' and `attemptConnect` retried a grant that no
 *  credential refresh can ever produce - the same never-terminating loop
 *  DEC-568 exists to end, pointed the other way. */
async function resyncAndProbe(b: Bound): Promise<'ok' | 'revoked' | 'denied' | 'error'> {
  const conn = getConnection(b.id);
  if (!conn) return 'error';
  try {
    // Always fetch the FULL set (since=0): the buffer is tiny (a handful of
    // peer bridges), and a full authoritative fetch avoids the cursor-drift +
    // client-clock-skew bugs a since=<us> cursor invites, and lets us REPLACE
    // the registry so entries the server no longer returns (e.g. after a daemon
    // restart wiped its buffer) get pruned instead of lingering.
    const resp = await conn.httpClient().getInheritances(0);
    if (bound !== b) return 'ok';   // rebound mid-fetch - drop this stale result
    replaceRegistry(resp.inheritances);
    return 'ok';
  } catch (err) {
    // 403 COUNTS, and missing it is why a revoked bearer retried forever.
    // OPBridge's `PairingToken::covers()` checks `revoked` BEFORE scope, and
    // both paths return the same "token scope does not include this endpoint"
    // message - so a REVOKED-but-recognised token arrives here as 403, not
    // 401, and this probe read it as an ordinary error and kept polling.
    // Measured on the operator's rig 2026-09-07: the dev PC 403'd
    // `/toggles/opbuttons`, `/engines/opbuttons/state` and
    // `/timelines/opbuttons/state` once a second while the PWA showed
    // "connected", because nothing here classified it as revocation.
    //
    // PREFER `reason` OVER THE STATUS - OPBridge's DEC-568 (2026-09-07) makes
    // the status alone actively misleading in the OTHER direction once it
    // ships: revoked goes back to 401, and 403 comes to mean `scope_not_granted`
    // for a LIVE token, which no credential refresh can fix. So a status-only
    // 403 arm would spin re-fetching a bearer that is working exactly as issued.
    //
    // Their contract: 401 + bearer_missing / bearer_unknown / bearer_revoked /
    // bearer_expired -> get a new credential. 403 + scope_not_granted -> surface,
    // never retry. `reason` rides beside the existing prose `error`, so it is
    // additive and absent on older daemons.
    //
    // The body is already embedded in the thrown message, so this needs no new
    // plumbing through OpbridgeError.
    if (err instanceof OpbridgeError) {
      const reason = /"reason"\s*:\s*"([a-z_]+)"/.exec(err.message)?.[1] ?? null;
      // Branch on `reason`, never the prose, and never the status alone -
      // OPBridge's auth.rs states the full set. Mapped explicitly rather than
      // by `startsWith('bearer_')`: that prefix test silently sent
      // `scope_not_granted` into the retry arm, and it would send any future
      // non-bearer_ reason there too.
      switch (reason) {
        // The credential is no good. Remedy: refresh via inheritance, or re-pair.
        case 'bearer_revoked':
        case 'bearer_expired':
        case 'bearer_unknown':
        case 'bearer_missing':
          return 'revoked';
        // The credential is LIVE and simply lacks the grant. No refresh can add
        // a grant that was never issued, so retrying is an infinite loop by
        // construction. Surface and stop.
        case 'scope_not_granted':
          return 'denied';
        // Recoverable by waiting - the caller's backoff is the right remedy.
        case 'rate_limited':
          return 'error';
        default:
          break;      // no reason, or one we do not know yet - fall through
      }

      // ⚠️ THE STATUS FALLBACK STAYS, and NEXT.md's "drop the status arms" is
      // wrong on the evidence. MEASURED on the wire 2026-09-07 against the
      // running v1.15.119+8b46f00, the build that ships DEC-568:
      //
      //   GET /pairing/inheritances (bad bearer) -> 401 {"error":"invalid bearer"}
      //                                             ^ NO `reason` field at all
      //
      // DEC-568's envelope comes from `require_scope`, and `pairing.rs::
      // inheritances` does NOT use it - it does its own inline lookup and
      // returns push.rs's bare `error()`, which is `{"error": msg}`, for BOTH
      // "invalid bearer" and "bearer revoked" two lines apart. Seven paths in
      // that tree emit "unknown token"; only auth.rs:167 attaches a reason.
      //
      // So THIS probe - the one revocation recovery depends on - is precisely
      // the endpoint that cannot answer reason-first. Going reason-only here
      // would classify a revoked bearer as a transient error and retry it
      // forever, which is the exact bug DEC-568 was written to end.
      //
      // Costs nothing when a reason IS present: the switch above returns first.
      //
      // ⚠️ THE OLD "delete this once the endpoint carries a reason" CONDITION
      // IS NOW MET, AND IT IS STILL NOT A REASON TO DELETE IT. Measured on
      // v1.15.120+da56b21 (both machines), that endpoint now answers
      // `{"error":"invalid bearer","reason":"bearer_unknown"}`. Kept anyway:
      //
      //   1. That measurement covers the LOOKUP-FAILED branch. The REVOKED
      //      branch is a different call (`reject_if_not_live`) and still has
      //      no wire measurement on either side - nobody can mint a revoked
      //      plaintext to probe with. Deleting on the strength of a sibling
      //      branch is the same source-read-as-proof this comment exists to
      //      warn against.
      //   2. Not every bridge on a LAN is this build. A rescue install, an
      //      un-updated peer, or an older daemon at a venue answers without a
      //      reason, and this arm is the only thing standing between that and
      //      an un-terminating retry of a dead credential.
      //   3. Removing it buys NOTHING. It is unreachable whenever a reason is
      //      present, so the only outcome of deleting it is losing a net.
      //
      // Treat it as permanent. `auth_reason_classifies_and_denies` RUN 3 goes
      // red if it is removed.
      if (err.kind === 'unauthorized' || err.httpStatus === 401 || err.httpStatus === 403) {
        return 'revoked';
      }
    }
    return 'error';
  }
}

function onRevoked(b: Bound): void {
  if (bound !== b) return;
  setRevokedSig(true);
  setConnectedSig(false);
  closeSocket();          // leaves `bound` intact so the re-probe knows the bearer
  log('revoked');
  startRevokedReprobe();
}

/** DEC-568 `scope_not_granted`: the bearer is live and simply lacks the grant.
 *  TERMINAL for this binding - deliberately does NOT start a re-probe, because
 *  no amount of polling can add a scope the token was never issued with. The
 *  only exits are a re-pair or a rebind to a different bridge, and both replace
 *  `bound`, which clears this via `stopConsumerWs`/rebind. */
function onDenied(b: Bound): void {
  if (bound !== b) return;
  setDeniedSig(true);
  setConnectedSig(false);
  closeSocket();
  log('denied');
}

// ─── inbound event routing ──────────────────────────────────────────────────

function handleMessage(raw: string): void {
  if (!raw) return;
  let msg: Record<string, unknown>;
  try {
    const parsed = JSON.parse(raw) as unknown;
    if (!parsed || typeof parsed !== 'object') return;
    msg = parsed as Record<string, unknown>;
  } catch {
    return;
  }
  watchJob(`ws:${String(msg['event'])}`);
  switch (msg['event']) {
    case 'inheritance_available': {
      // WS event renames `bridge_instance_name` -> `instance_name` vs the poll.
      const inh = wsEventToInheritance(msg);
      if (inh) {
        mergeInheritances([inh]);
        if (Number.isFinite(inh.minted_at_us)) cursorUs = Math.max(cursorUs, inh.minted_at_us);
      }
      break;
    }
    case 'clock_marker': {
      for (const fn of clockMarkerListeners) { try { fn(msg); } catch { /* */ } }
      break;
    }
    case 'bearer_revoked': {
      // Server already filtered this to OUR bearer (matched on prefix_id
      // before forwarding), so any frame with this event IS about this
      // socket — no identity re-check needed client-side.
      if (bound) onRevoked(bound);
      break;
    }
    case 'midi_in': {
      const evt = parseMidiIn(msg);
      if (evt) {
        midiInPushSeen = true;
        const ts = msg['timestamp_us'];
        if (typeof ts !== 'number' || claimMidiIn(evt.port, ts, evt.bytes)) deliverMidiIn(evt);
      }
      break;
    }
    case 'pwa_action': {
      const command = msg['command'];
      if (typeof command === 'string') {
        const ruleId = msg['rule_id'];
        const evt: PwaActionEvent = {
          ruleId: typeof ruleId === 'string' ? ruleId : '',
          command,
          args: msg['args'],
          bridgeId: bound?.id,
        };
        deliverPwaAction(evt);
      }
      break;
    }
    case 'timeline_fired': {
      const evt = parseTimelineFired(msg, bound?.id);
      if (evt) deliverTimelineFired(evt);
      break;
    }
    case 'control': {
      const evt = parseControlEvent(msg, bound?.id);
      if (evt) deliverControlEvent(evt);
      break;
    }
    case 'rule_fired':
    case 'action_fired': {
      const evt = parseFiredEvent(msg, bound?.id);
      if (evt) deliverFired(evt);
      break;
    }
    case 'setlist_update': {
      // The song-change push (OPBridge DEC-585). Change-driven, no sampler,
      // silent while nothing happens.
      //
      // TWO frames per song change is NORMAL, not a bug: `record_song_change`
      // and `mark_sent` are both publish points, and the second is the one
      // carrying the populated `last_sent`. `recordSetlistState` sequences
      // them by `generation` rather than by arrival, because the watch
      // channel coalesces and arrival order is not reliable.
      //
      // The frame body IS the `GET /setlist/state` body plus `event`, built
      // by the same function on their side, so one shape serves both paths.
      recordSetlistState(msg as unknown as SetlistStateResponse);
      break;
    }
    case 'doc_updated': {
      // Wire shape captured off a live socket 2026-08-31, not inferred:
      //   {"event":"doc_updated","consumer":"opbuttons",
      //    "doc_id":"__smoke_wsprobe","rev":1,"deleted":false}
      // Metadata only, by OPBridge's deliberate design - it says "something
      // changed, go re-pull", it does not carry the body.
      const consumer = msg['consumer'];
      const docId = msg['doc_id'];
      if (typeof consumer === 'string' && typeof docId === 'string') {
        const rev = msg['rev'];
        const evt: DocUpdatedEvent = {
          consumer,
          docId,
          rev: typeof rev === 'number' ? rev : 0,
          deleted: msg['deleted'] === true,
        };
        for (const fn of docUpdatedListeners) { try { fn(evt); } catch { /* */ } }
      }
      break;
    }
    case 'opvisual_status': {
      // OPVisual's own report (its CONTRACT.md section 8), relayed by the bridge
      // OPVisual is attached to: state, heartbeat, grid_fired, grid_column.
      // Kept per bridge row in `opvisual/live.ts` (DEC-272).
      if (bound) ingestOpvStatus(bound.id, msg);
      break;
    }
    case 'performance_mode_changed': {
      // The ONE suite-wide performance switch (OPBridge PROTOCOL.md
      // "Performance mode - ONE suite-wide switch", S284/S285). Sent to every
      // consumer socket whatever its scope:
      //   {"event":"performance_mode_changed","enabled":true,
      //    "source":{"app":"opvisual","app_name":"OPVisual","machine":"Mac Studio"}}
      // `source` is ABSENT (not null) when unknown - a pre-S285 switch or a
      // mesh peer on an older build.
      const enabled = msg['enabled'];
      if (typeof enabled === 'boolean') {
        const evt: PerformanceModeChangedEvent = { enabled, source: parsePerformanceModeSource(msg['source']) };
        for (const fn of performanceModeListeners) { try { fn(evt); } catch { /* */ } }
      }
      break;
    }
    default:
      // Forward-compat: tolerate + ignore any event we don't model
      // (`bundle_expanded`, future kinds).
      break;
  }
}

/** Who switched performance mode, as OPBridge reports it. `app` is the
 *  consumer id (match on this); the other two are display names. */
export interface PerformanceModeSource {
  app: string;
  appName: string | null;
  machine: string | null;
}

/** A `performance_mode_changed` frame. `source` is null when OPBridge did
 *  not say who switched it. */
export interface PerformanceModeChangedEvent {
  enabled: boolean;
  source: PerformanceModeSource | null;
}

/** Parse the `source` object shared by the push frame and `/health`'s
 *  `capabilities.performance_mode_source`. Null for absent or malformed. */
export function parsePerformanceModeSource(raw: unknown): PerformanceModeSource | null {
  if (!raw || typeof raw !== 'object') return null;
  const o = raw as Record<string, unknown>;
  if (typeof o['app'] !== 'string' || !o['app']) return null;
  return {
    app: o['app'],
    appName: typeof o['app_name'] === 'string' && o['app_name'] ? o['app_name'] : null,
    machine: typeof o['machine'] === 'string' && o['machine'] ? o['machine'] : null,
  };
}

/** Subscribe to `performance_mode_changed` frames. Returns an unsubscribe fn. */
export function onPerformanceModeChanged(fn: (e: PerformanceModeChangedEvent) => void): () => void {
  performanceModeListeners.add(fn);
  return () => performanceModeListeners.delete(fn);
}

function wsEventToInheritance(msg: Record<string, unknown>): PairingInheritance | null {
  const instance = msg['instance_name'];
  const bearer = msg['bearer'];
  const consumer = msg['consumer'];
  const userId = msg['user_id'];
  const nodeId = msg['node_id'];
  if (typeof instance !== 'string' || !instance) return null;
  if (typeof bearer !== 'string' || !bearer) return null;
  return {
    bridge_instance_name: instance,
    // node_id is the address-resolvable key (DEC-465); the drainer joins it
    // against /peers/paired-bridges. Absent on pre-v1.15.8 daemon pushes.
    node_id: typeof nodeId === 'string' ? nodeId : '',
    bearer,
    consumer: typeof consumer === 'string' ? consumer : '',
    user_id: typeof userId === 'string' ? userId : '',
    scope_bundle_id: typeof msg['scope_bundle_id'] === 'string' ? (msg['scope_bundle_id'] as string) : '',
    scope_bundle_version: typeof msg['scope_bundle_version'] === 'number' ? (msg['scope_bundle_version'] as number) : 0,
    minted_at_us: typeof msg['minted_at_us'] === 'number' ? (msg['minted_at_us'] as number) : 0,
  };
}

/** Stable registry key for an inheritance: the minting bridge's `node_id`
 *  (DEC-465) when present, else its mDNS `bridge_instance_name` for
 *  pre-v1.15.8 daemon records. Keying by node_id dedups correctly even if a
 *  bridge's mDNS label changes between two mints. */
function inheritanceKey(inh: PairingInheritance): string {
  return inh.node_id || inh.bridge_instance_name;
}

/** Replace the registry with the server's authoritative full set (used by the
 *  resync/probe path). Prunes anything the server no longer returns, and fires
 *  the "new capabilities" marker only for instances not previously known. */
function replaceRegistry(list: PairingInheritance[]): void {
  const before = new Map<string, string>([...registry].map(([k, v]) => [k, v.bearer]));
  registry.clear();
  let sawNew = false;
  let changed = false;
  for (const inh of list) {
    if (!inh || !inh.bearer) continue;
    const key = inheritanceKey(inh);
    if (!key) continue;
    registry.set(key, inh);
    const prevBearer = before.get(key);
    if (prevBearer === undefined) { sawNew = true; changed = true; }
    else if (prevBearer !== inh.bearer) { changed = true; }
    if (Number.isFinite(inh.minted_at_us)) cursorUs = Math.max(cursorUs, inh.minted_at_us);
  }
  if (registry.size !== before.size) changed = true;   // a key was removed
  // Only emit when the set actually changed - the probe re-fetches every 45s,
  // so an unconditional push would re-render every availableInheritances()
  // reader with byte-identical data.
  if (changed) pushRegistry();
  if (sawNew) setLastInheritanceAtSig(Date.now());
}

/** Upsert inheritances into the registry keyed by peer instance name, keeping
 *  the newest by `minted_at_us`. Used by the live WS push (incremental). Fires
 *  the "new capabilities" marker only for a genuinely-new (or newer) entry. */
function mergeInheritances(list: PairingInheritance[]): void {
  let sawNew = false;
  for (const inh of list) {
    if (!inh || !inh.bearer) continue;
    const key = inheritanceKey(inh);
    if (!key) continue;
    const prev = registry.get(key);
    if (!prev || inh.minted_at_us > prev.minted_at_us || inh.bearer !== prev.bearer) {
      registry.set(key, inh);
      sawNew = true;
    }
  }
  if (sawNew) {
    pushRegistry();
    setLastInheritanceAtSig(Date.now());
  }
}

function pushRegistry(): void {
  const arr = [...registry.values()].sort((a, b) => b.minted_at_us - a.minted_at_us);
  setInheritancesSig(arr);
}

// ─── timers ─────────────────────────────────────────────────────────────────

function startProbeTimer(): void {
  stopProbeTimer();
  probeTimer = setInterval(() => {
    const b = bound;
    if (!alive || !b || !socket || socket.readyState !== WebSocket.OPEN) return;
    void resyncAndProbe(b).then((res) => {
      if (bound !== b) return;
      if (res === 'revoked') onRevoked(b);
      else if (res === 'denied') onDenied(b);
    });
  }, PROBE_INTERVAL_MS);
}

function stopProbeTimer(): void {
  if (probeTimer) { clearInterval(probeTimer); probeTimer = null; }
}

function startRevokedReprobe(): void {
  stopRevokedReprobe();
  revokedTimer = setInterval(() => {
    const b = bound;
    if (!alive || !b || !revokedSig()) { stopRevokedReprobe(); return; }
    void resyncAndProbe(b).then((res) => {
      if (bound !== b) return;
      if (res === 'ok') {
        // Access restored (operator re-approved / same bearer valid again).
        stopRevokedReprobe();
        setRevokedSig(false);
        reconnectAttempt = 0;
        attemptConnect();
      }
    });
  }, REVOKED_REPROBE_MS);
}

function stopRevokedReprobe(): void {
  if (revokedTimer) { clearInterval(revokedTimer); revokedTimer = null; }
}

// ─── helpers ────────────────────────────────────────────────────────────────

function closeSocket(): void {
  if (reconnectTimer) { clearTimeout(reconnectTimer); reconnectTimer = null; }
  stopProbeTimer();
  const s = socket;
  socket = null;   // null BEFORE close() so the 'close' handler's guard skips reconnect
  if (s) { try { s.close(); } catch { /* */ } }
  setConnectedSig(false);
}

function stripSlash(url: string): string {
  return url.replace(/\/+$/, '');
}

function log(state: 'connected' | 'reconnecting' | 'revoked' | 'denied'): void {
  const name = bound ? bound.id : '?';
  const why = state === 'denied'
    ? ' - bearer is live but scope_not_granted; NOT retrying, a refresh cannot add a grant'
    : '';
  console.info(`[consumerWs] ${state} (primary=${name})${why}`);
}

/** Subscribe to `clock_marker` frames (Build 2a hook - unused this session).
 *  Returns an unsubscribe fn. */
export function onClockMarker(fn: (m: Record<string, unknown>) => void): () => void {
  clockMarkerListeners.add(fn);
  return () => clockMarkerListeners.delete(fn);
}

/** A `pwa` action addressed to this consumer: a Patchbay rule's (the B-path)
 *  or a timeline cue's (`timeline_light`, DEC-269). */
export interface PwaActionEvent {
  ruleId: string;
  command: string;
  args: unknown;
  /** The bridge whose socket delivered it: the primary's row id here, a
   *  peer's from `opbridge/peerPwaSockets.ts`. Undefined from the DEV
   *  injector. Lets a held timeline light be cleared when that bridge's
   *  song stops. */
  bridgeId?: string | undefined;
}

/** Subscribe to `pwa_action` frames. Returns an unsubscribe fn. */
export function onPwaAction(fn: (e: PwaActionEvent) => void): () => void {
  pwaActionListeners.add(fn);
  return () => pwaActionListeners.delete(fn);
}

function deliverPwaAction(evt: PwaActionEvent): void {
  for (const fn of pwaActionListeners) { try { fn(evt); } catch { /* */ } }
}

/** Deliver a `pwa_action` that arrived on a NON-primary bridge's socket
 *  (`opbridge/peerPwaSockets.ts`, DEC-269). Kept separate from
 *  `handleMessage`, like `emitMidiIn`, so a peer socket can only ever drive
 *  this one event type: inheritance, revocation, doc and setlist frames stay
 *  the primary's alone. */
export function emitPwaAction(evt: PwaActionEvent): void {
  deliverPwaAction(evt);
}

/** One timeline Cue step OPBridge dispatched (DEC-269 Phase B, OPBridge S314,
 *  `/health.capabilities.timeline_fired`). Frame:
 *  `{ event: "timeline_fired", timelineId, stepId, module, command, ok, result, error }`.
 *  `stepId` is exactly the cue id we pushed (`<step>`, `<step>-1`,
 *  `<step>-off`, ...). For `midi` / `osc`, `ok` is the PORT WRITE and the
 *  frame follows it; every other module reports `"dispatched"`. Lighting
 *  blocks and the engine's own rescue note-offs send no frame. */
export interface TimelineFiredEvent {
  timelineId: string;
  stepId: string;
  module: string;
  ok: boolean;
  error: string | null;
  /** The bridge whose socket delivered it (see {@link PwaActionEvent.bridgeId}). */
  bridgeId?: string | undefined;
}

/** Parse a `timeline_fired` frame; null when it is not one we can use. */
export function parseTimelineFired(msg: Record<string, unknown>, bridgeId: string | undefined): TimelineFiredEvent | null {
  const timelineId = msg['timelineId'];
  const stepId = msg['stepId'];
  if (typeof timelineId !== 'string' || typeof stepId !== 'string' || !stepId) return null;
  const error = msg['error'];
  return {
    timelineId,
    stepId,
    module: typeof msg['module'] === 'string' ? msg['module'] : '',
    // Anything but a literal `true` is a failure: a frame that cannot say it
    // succeeded must not light a control (DEC-124).
    ok: msg['ok'] === true,
    error: typeof error === 'string' ? error : null,
    bridgeId,
  };
}

/** Subscribe to `timeline_fired` frames, from the primary or a peer socket. */
export function onTimelineFired(fn: (e: TimelineFiredEvent) => void): () => void {
  timelineFiredListeners.add(fn);
  return () => timelineFiredListeners.delete(fn);
}

function deliverTimelineFired(evt: TimelineFiredEvent): void {
  for (const fn of timelineFiredListeners) { try { fn(evt); } catch { /* */ } }
}

/** A `timeline_fired` from a NON-primary bridge's socket (same reasoning as
 *  {@link emitPwaAction}). */
export function emitTimelineFired(evt: TimelineFiredEvent): void {
  deliverTimelineFired(evt);
}

/** One press, release or move of a controller OPBridge knows (controller
 *  codes, OPBridge S301; DEC-256). Frame: `{ event: "control", unitId,
 *  unitCode, controlId, code, name?, state, value, pageId, source, nodeId?,
 *  machineName? }`, for bearers holding `/controllers`. Presses and releases
 *  cross the mesh (S308), so one press can arrive on several of our sockets:
 *  a listener that acts on presses dedupes them. */
export interface ControlEvent {
  unitId: string;
  unitCode: string;
  controlId: string;
  code: string;
  name: string | null;
  state: 'press' | 'release' | 'move';
  value: number;
  /** The PC the controller is on, and its name (S308). */
  nodeId: string | null;
  machineName: string | null;
  bridgeId?: string | undefined;
}

/** Parse a `control` frame; null when it is not one we can use. */
export function parseControlEvent(msg: Record<string, unknown>, bridgeId: string | undefined): ControlEvent | null {
  const controlId = msg['controlId'];
  const state = msg['state'];
  if (typeof controlId !== 'string' || !controlId) return null;
  if (state !== 'press' && state !== 'release' && state !== 'move') return null;
  const str = (k: string): string | null => (typeof msg[k] === 'string' ? msg[k] as string : null);
  return {
    unitId: str('unitId') ?? '',
    unitCode: str('unitCode') ?? '',
    controlId,
    code: str('code') ?? '',
    name: str('name'),
    state,
    value: typeof msg['value'] === 'number' ? msg['value'] : 0,
    nodeId: str('nodeId'),
    machineName: str('machineName'),
    bridgeId,
  };
}

const controlListeners = new Set<(e: ControlEvent) => void>();

/** Subscribe to `control` frames, from the primary or a peer socket. */
export function onControlEvent(fn: (e: ControlEvent) => void): () => void {
  controlListeners.add(fn);
  return () => controlListeners.delete(fn);
}

function deliverControlEvent(evt: ControlEvent): void {
  for (const fn of controlListeners) { try { fn(evt); } catch { /* */ } }
}

/** A `control` frame from a NON-primary bridge's socket. */
export function emitControlEvent(evt: ControlEvent): void {
  deliverControlEvent(evt);
}

/** One of OUR rules fired on a bridge, or one of OUR named actions was pressed (OPBridge S324, `rule_fired_push`):
 *  `rule_fired { rule_id, consumer_id, ts_us, source }` (a MIDI trigger is `midiTrigger-<itemId>`, its release
 *  `midiTrigger-<itemId>-release`) and `action_fired { action_id, actor, ok, ts_us }` (a Stream Deck key, OPMod, the
 *  dashboard; `action_id` is `<itemId>` or `<itemId>.<key>`). Sent by the bridge where it happened, not relayed, so
 *  the peer sockets deliver them too. */
export type FiredEvent =
  | { kind: 'rule'; ruleId: string; tsUs: number | null; bridgeId?: string | undefined }
  | { kind: 'action'; actionId: string; actor: string | null; ok: boolean; tsUs: number | null; bridgeId?: string | undefined };

export function parseFiredEvent(msg: Record<string, unknown>, bridgeId: string | undefined): FiredEvent | null {
  const ts = typeof msg['ts_us'] === 'number' ? msg['ts_us'] as number : null;
  if (msg['event'] === 'rule_fired') {
    const ruleId = msg['rule_id'];
    return typeof ruleId === 'string' && ruleId ? { kind: 'rule', ruleId, tsUs: ts, bridgeId } : null;
  }
  if (msg['event'] === 'action_fired') {
    const actionId = msg['action_id'];
    if (typeof actionId !== 'string' || !actionId) return null;
    return { kind: 'action', actionId, actor: typeof msg['actor'] === 'string' ? msg['actor'] as string : null, ok: msg['ok'] !== false, tsUs: ts, bridgeId };
  }
  return null;
}

const firedListeners = new Set<(e: FiredEvent) => void>();
export function onFired(fn: (e: FiredEvent) => void): () => void {
  firedListeners.add(fn);
  return () => firedListeners.delete(fn);
}
function deliverFired(evt: FiredEvent): void {
  for (const fn of firedListeners) { try { fn(evt); } catch { /* */ } }
}
/** A `rule_fired` / `action_fired` frame from a NON-primary bridge's socket. */
export function emitFired(evt: FiredEvent): void {
  deliverFired(evt);
}

/** A doc-store document changed on the bridge (any writer - this device,
 *  another device, or eventually a mesh peer). Metadata only. */
export interface DocUpdatedEvent {
  consumer: string;
  docId: string;
  rev: number;
  deleted: boolean;
}

/**
 * Subscribe to `doc_updated` frames. Returns an unsubscribe fn.
 *
 * ✅ VERIFIED LIVE 2026-08-31, which is worth stating because OPBridge's own
 * design notes flagged it as the one unproven link: they had read all three
 * source sites (`handlers/docs.rs` publish, `handlers/consumer_ws.rs` emit,
 * the `opbuttons-full` scope grant) and confirmed the scope math, but recorded
 * that nobody had ever watched a frame actually land at an OPButtons socket,
 * and asked us to log one before relying on it. Done: a `PUT` to a throwaway
 * doc produced the frame quoted in `handleMessage` above, on this exact
 * socket, within ~50ms.
 */
export function onDocUpdated(fn: (e: DocUpdatedEvent) => void): () => void {
  docUpdatedListeners.add(fn);
  return () => docUpdatedListeners.delete(fn);
}

// ─── inbound MIDI (`midi_in`) ───────────────────────────────────────────────
//
// OPBridge contract, agreed 2026-08-27 (`OPApps/opbridge-opbuttons.md`): a THIRD
// `/ws/consumer` arm carrying inbound MIDI, gated on the `/midi/recent` scope
// we already hold — so no scope-bundle change and NO RE-PAIR.
//
// ⚠️ NOT BUILT ON THE OPBRIDGE SIDE YET. Their words: "Not yet scheduled, and
// we are not going to pretend otherwise." This arm is deliberately built ahead
// of theirs so both sides are not sitting on a silent counterpart — before it,
// a `midi_in` frame would have hit `handleMessage`'s forward-compat `default:`
// and been dropped on the floor with no trace.
//
// Why this replaced a legacy path rather than adding a new capability: the
// PWA's only inbound-MIDI route was the legacy Node bridge's `bridge.on(
// 'midi.in')`, and `packages/bridge/` was deleted 2026-08-20. StatusLed
// (DEC-041) and MIDI-triggers-a-button (DEC-112/154) have had NO working path
// since. OPBridge has accepted this is a REGRESSION, not a net-new ask.
//
// ⚠️ `/ws` is NOT an alternative shortcut. It requires an explicit `/ws` scope,
// and `opbuttons-full`'s 35 scopes contain neither `/ws` nor `/ws/consumer`
// (read off the running daemon by OPBridge, not parsed from source), so a `/ws`
// connect would 403 today. `/ws/consumer` authorizes the CONNECTION on any
// valid bearer then gates each message TYPE on its REST equivalent's scope.

/**
 * One inbound MIDI message pushed from a bridge.
 *
 * **Two node ids, deliberately.** OPBridge asked which one we key on and we
 * answered BOTH (2026-08-27): they join on `node_id` via `findBridgeByNode`,
 * so they need one that resolves to a bridge row they hold, and on a mesh the
 * node that physically OWNS the port differs from the peer that DELIVERED the
 * frame. Our per-item bridge affinity (DEC-169) keys on the owner; the via-node
 * is what a relay-aware diagnostic needs. Parsed tolerantly so a daemon that
 * ships only the flat `node_id` from their original proposal still works.
 *
 * `kind` is kept as a plain string rather than a union: the exact vocabulary is
 * OPBridge's to fix when they build the arm, and enum-locking a guess here
 * would turn an unmodelled-but-valid kind into a silently dropped event.
 */
export interface MidiInEvent {
  /** Node that physically owns the input port. Affinity keys on this. */
  portOwnerNodeId: string;
  /** Node that delivered the frame; differs from the owner on a relay. */
  viaNodeId: string;
  /** Input port display name, e.g. `"Midi Fighter Twister"`. */
  port: string;
  /** MIDI channel as OPBridge reports it. ⚠️ Channel base is INCONSISTENT
   *  across this codebase (0- vs 1-based per item kind) — do not assume; the
   *  consumer that compares against an item's channel must reconcile. */
  channel: number;
  /** e.g. `"cc"` / `"note_on"` / `"note_off"` / `"pc"`. Not enum-locked. */
  kind: string;
  /** CC number, note number or program number depending on `kind`. */
  number: number;
  /** CC value or note velocity. */
  value: number;
  /** Raw bytes when the daemon supplies them; `[]` when absent. */
  bytes: number[];
}

const midiInListeners = new Set<(e: MidiInEvent) => void>();
/** Observers that must NOT count as consumers. The DEV test hook taps every
 *  event, and if that tap counted, `midiInListenerCount()` would never read
 *  zero in a dev build — making `midiInPoll`'s "do not drain a destructive
 *  route when nobody is listening" guard vacuous in exactly the builds the
 *  smokes run against. */
const midiInDiagnostics = new Set<(e: MidiInEvent) => void>();

/**
 * Deliver one inbound MIDI event to every subscriber.
 *
 * ⚠️ **Never throttle, sample, coalesce or de-duplicate here, and never wrap a
 * batch of these in Solid's `batch()`.** Rate-limiting the MESSAGE stream is
 * the intuitive way to handle a firehose — a Midi Fighter Twister emits ~142
 * CC messages per knob turn (measured 2026-08-27) — and it is the wrong layer.
 * A discrete button press is 2 messages and can land between two encoder
 * ticks, so any sampling here silently eats the one event that mattered while
 * the harmless spam survives.
 *
 * The correct place to absorb the firehose is the RENDER, not the feed. Open
 * Stage Control (the closest prior art: node daemon + browser UI, same
 * problem) applies every message to widget state and coalesces only the
 * redraw, keyed per widget, one paint per frame — so nothing is ever dropped,
 * only redundant draws are. Solid gives us the equivalent for free: a signal
 * write with an unchanged value notifies nothing, so a StatusLed whose lit
 * state did not change costs no DOM work no matter how many CCs stream past.
 *
 * If a future consumer needs decimation, it decimates for itself — the shared
 * sink stays lossless.
 */
function deliverMidiIn(evt: MidiInEvent): void {
  for (const fn of midiInListeners) { try { fn(evt); } catch { /* */ } }
  for (const fn of midiInDiagnostics) { try { fn(evt); } catch { /* */ } }
}

/** Number of REAL inbound-MIDI consumers (diagnostics excluded). Read by
 *  `midiInPoll` to decide whether draining `/midi/recent` is warranted. */
export function midiInListenerCount(): number {
  return midiInListeners.size;
}

function numOr(v: unknown, fallback: number): number {
  return typeof v === 'number' && Number.isFinite(v) ? v : fallback;
}

function strOr(v: unknown, fallback: string): string {
  return typeof v === 'string' ? v : fallback;
}

/** Parse a `midi_in` frame. Returns null for a frame missing the fields that
 *  carry meaning, so a malformed push is ignored rather than delivered as a
 *  fabricated CC 0 on channel 0 — which would fire real bindings. */
/**
 * Map a status byte (channel nibble masked or not) to the contract's `kind`.
 * Shared by the push arm and `midiInPoll` so the two transports cannot
 * disagree. A note-on with velocity 0 IS a note-off - MIDI's own convention,
 * and OPBridge's `note_edge` decodes it the same way; reporting it as `note_on`
 * would leave a consumer holding a note that never releases.
 */
export function midiKindFor(status: number, data2: number | undefined): string | null {
  switch (status & 0xf0) {
    case 0x80: return 'note_off';
    case 0x90: return (data2 ?? 0) > 0 ? 'note_on' : 'note_off';
    case 0xb0: return 'cc';
    case 0xc0: return 'pc';
    default:   return null;   // pitch-bend, aftertouch, sysex, real-time
  }
}

/**
 * Parse a pushed `midi_in` frame.
 *
 * ⚠️ 2026-10-16: this used to read only a `kind` + `number` + `value` shape -
 * the one PROPOSED before OPBridge built the arm. OPBridge shipped (S242,
 * `consumer_ws.rs::midi_in_payload`) the shape its own `/midi/recent` ring
 * uses: `status` (channel nibble masked), `channel` (0-based), `data1`,
 * `data2`, with `kind` as `noteon`/`noteoff`/`cc`/`program`/... So every real
 * frame failed the `number` check and was dropped, while the smoke stayed
 * green on hand-written frames in the proposed shape. The canonical path is
 * now OPBridge's fields, decoded exactly as `midiInPoll` decodes the ring; the
 * proposed shape is still accepted for a daemon that sends it.
 */
function parseMidiIn(msg: Record<string, unknown>): MidiInEvent | null {
  const port = msg['port'];
  if (typeof port !== 'string' || !port) return null;
  const rawBytes = msg['bytes'];
  const base = {
    portOwnerNodeId: strOr(msg['port_owner_node_id'], strOr(msg['node_id'], '')),
    viaNodeId: strOr(msg['via_node_id'], ''),
    port,
    bytes: Array.isArray(rawBytes) ? rawBytes.filter((b): b is number => typeof b === 'number') : [],
  };
  const status = msg['status'];
  const data1 = msg['data1'];
  if (typeof status === 'number' && Number.isFinite(status)) {
    // OPBridge's shape. An absent channel is a non-channel-voice message
    // (OPBridge omits it rather than zeroing it), never channel 0.
    const channel = msg['channel'];
    if (typeof channel !== 'number' || typeof data1 !== 'number' || !Number.isFinite(data1)) return null;
    const d2 = msg['data2'];
    const kind = midiKindFor(status, typeof d2 === 'number' ? d2 : undefined);
    if (kind === null) return null;
    return { ...base, channel, kind, number: data1, value: typeof d2 === 'number' ? d2 : 0 };
  }
  // The proposed shape. `number` is what a binding matches on; a frame
  // without it cannot be routed, and defaulting it to 0 would collide with a
  // real CC 0.
  const kind = msg['kind'];
  if (typeof kind !== 'string' || !kind) return null;
  const number = msg['number'];
  if (typeof number !== 'number' || !Number.isFinite(number)) return null;
  return { ...base, channel: numOr(msg['channel'], 0), kind, number, value: numOr(msg['value'], 0) };
}

/** True once the current `/ws/consumer` socket has delivered a parsed
 *  `midi_in` frame (reset when the socket opens or closes). Diagnostic only:
 *  the `/midi/recent` poll does NOT stand down on it, because on the
 *  operator's Mac the push carried some ports and not others (midiInPoll.ts). */
let midiInPushSeen = false;
export function midiInPushLive(): boolean {
  return midiInPushSeen && connectedSig();
}

/** The last inbound messages delivered, by port, OPBridge's own `timestamp_us` and the bytes, whichever transport
 *  delivered them (2026-10-16, measured on the Mac: after the push stood the poll down, OPBridge's ring kept every
 *  message, and the poll's first drain after it resumed handed them over a second time; and until the first push
 *  frame of a session, one press can reach both). The push frame and the ring entry carry the daemon's timestamp for
 *  the same message, so the match is exact; a message only one transport saw still arrives. Kept across reconnects,
 *  which is when it matters. */
const deliveredKeys = new Set<string>();
const deliveredOrder: string[] = [];
const DELIVERED_KEEP = 1024;
/** True the first time this message is offered (deliver it); false when either transport already delivered it. */
export function claimMidiIn(port: string, timestampUs: number, bytes: readonly number[]): boolean {
  if (bytes.length === 0) return true;
  const k = `${port}|${timestampUs}|${bytes.join(',')}`;
  if (deliveredKeys.has(k)) return false;
  deliveredKeys.add(k);
  deliveredOrder.push(k);
  if (deliveredOrder.length > DELIVERED_KEEP) deliveredKeys.delete(deliveredOrder.shift()!);
  return true;
}

/** Subscribe to inbound MIDI pushed over `/ws/consumer`. Returns an
 *  unsubscribe fn. This is the single sink every inbound-MIDI consumer should
 *  register on, so that if a transitional `/midi/recent` poll is added it can
 *  feed the SAME sink and be deleted later without touching any consumer. */
export function onMidiIn(fn: (e: MidiInEvent) => void): () => void {
  midiInListeners.add(fn);
  return () => midiInListeners.delete(fn);
}

/** Deliver a `midi_in` event from a non-WS source (a transitional poll).
 *  Kept separate from `handleMessage` so the poll cannot accidentally drive
 *  any other event type, and so deleting the poll removes exactly one caller. */
export function emitMidiIn(evt: MidiInEvent): void {
  deliverMidiIn(evt);
}

// ─── DEV test hook (drives production paths without a live socket) ───────────

declare global {
  interface Window {
    __opbuttonsConsumer?: {
      connected(): boolean;
      revoked(): boolean;
      denied(): boolean;
      inheritances(): PairingInheritance[];
      cursor(): number;
      injectEvent(event: Record<string, unknown>): void;
      injectRevoked(): void;
      /** Every `midi_in` event that survived parsing, oldest-first. Lets a
       *  smoke assert on what the REAL routing + parser produced rather than
       *  re-deriving the mapping in the test. */
      midiInSeen(): MidiInEvent[];
      clearMidiInSeen(): void;
      /** Register a REAL consumer, so `midiInListenerCount()` becomes
       *  non-zero and `midiInPoll` starts draining. Lets a smoke exercise
       *  the poll's own gate rather than bypassing it. */
      subscribeMidiIn(): void;
      midiInListenerCount(): number;
      /** As a socket reconnect does: the poll is no longer stood down (the
       *  push must prove itself again); what the push delivered is remembered. */
      forgetMidiInPush(): void;
      midiInPushLive(): boolean;
    };
  }
}

if (typeof window !== 'undefined' && import.meta.env.DEV) {
  const seen: MidiInEvent[] = [];
  // Registered as a DIAGNOSTIC, not a consumer - see `midiInDiagnostics`.
  midiInDiagnostics.add((e) => { seen.push(e); });
  window.__opbuttonsConsumer = {
    connected: () => connectedSig(),
    revoked: () => revokedSig(),
    denied: () => deniedSig(),
    inheritances: () => inheritancesSig(),
    cursor: () => cursorUs,
    injectEvent: (event) => handleMessage(JSON.stringify(event)),
    injectRevoked: () => { if (bound) onRevoked(bound); },
    midiInSeen: () => seen.slice(),
    clearMidiInSeen: () => { seen.length = 0; },
    subscribeMidiIn: () => { onMidiIn(() => { /* presence is the point */ }); },
    midiInListenerCount: () => midiInListenerCount(),
    forgetMidiInPush: () => { midiInPushSeen = false; },
    midiInPushLive: () => midiInPushLive(),
  };
}
